import type { CSSProperties } from "react";
import { formatMoney, initials, switchStyle } from "@cliqsuite/ui";

export { initials, switchStyle };

/** Format a Myanmar Kyat amount, e.g. mmk(150000) -> "MMK 150,000". */
export function mmk(n: number): string {
  const v = Math.round(Number(n) || 0);
  const a = Math.abs(v);
  if (a >= 1e6) {
    const millions = v / 1e6;
    const fixed = a >= 1e7 ? millions.toFixed(0) : millions.toFixed(1).replace(/\.0$/, "");
    return "MMK " + fixed + "M";
  }
  return formatMoney(v);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** e.g. "Jun 18, 2026" */
export function fmtDate(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** e.g. "Jun 18" (drops the year, used in dense table rows) */
export function fmtDateShort(iso: string | Date): string {
  return fmtDate(iso).replace(/, \d{4}$/, "");
}

export function addMonths(iso: string, months: number): string {
  const d = new Date(iso);
  d.setMonth(d.getMonth() + months);
  return fmtDate(d);
}

/** Same math as addMonths, but returns an ISO timestamp for writing to a DB column instead of a display string. */
export function addMonthsIso(iso: string, months: number): string {
  const d = new Date(iso);
  d.setMonth(d.getMonth() + months);
  return d.toISOString();
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function capitalize(s: string): string {
  return s ? s[0]!.toUpperCase() + s.slice(1) : s;
}

export function fmtAuditTime(iso: string): string {
  const d = new Date(iso);
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  // Canvas v2-a-audit: the time today, "Yesterday", then just the date.
  const days = Math.round((new Date().setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86_400_000);
  if (days === 0) return `${h}:${m} ${ap}`;
  if (days === 1) return "Yesterday";
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

const AVATAR_PALETTE = ["#5B8DEF", "#C173C9", "#54B08A", "#E0894A", "#8B5CC7", "#2D6FE0"];

export function avColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h + seed.charCodeAt(i)) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[h]!;
}

export function methodMeta(m: string): { icon: string; color: string } {
  return (
    (
      {
        qr: { icon: "qr_code_2", color: "#2D6FE0" },
        bank: { icon: "account_balance", color: "#8B5CC7" },
        wallet: { icon: "currency_bitcoin", color: "#21A35B" },
      } as Record<string, { icon: string; color: string }>
    )[m] ?? { icon: "payments", color: "#6B6F79" }
  );
}

export interface StatusMeta {
  label: string;
  fg: string;
  bg: string;
}

export function statusMeta(s: string): StatusMeta {
  return (
    (
      {
        pending: { label: "Pending", fg: "#8A5D00", bg: "#FCF1D4" },
        approved: { label: "Approved", fg: "#1E8E55", bg: "#E7F6EE" },
        rejected: { label: "Rejected", fg: "#DC2D49", bg: "#FFF1F4" },
      } as Record<string, StatusMeta>
    )[s] ?? { label: capitalize(s), fg: "#5F6470", bg: "#EEEFF3" }
  );
}

/** Business subscription_status -> chip meta (real enum from core.businesses). */
export function subscriptionStatusMeta(s: string): StatusMeta {
  return (
    (
      {
        active: { label: "Active", fg: "#1E8E55", bg: "#E7F6EE" },
        trial: { label: "Trial", fg: "#2D64C8", bg: "#EAF0FB" },
        inactive: { label: "Inactive", fg: "#5F6470", bg: "#EEEFF3" },
        expired: { label: "Past due", fg: "#8A5D00", bg: "#FCF1D4" },
        suspended: { label: "Suspended", fg: "#DC2D49", bg: "#FFF1F4" },
      } as Record<string, StatusMeta>
    )[s] ?? { label: capitalize(s), fg: "#5F6470", bg: "#EEEFF3" }
  );
}

/** The status's colours on an `o-pill` label (its shape comes from portal.css). */
export function chipStyle(m: StatusMeta): CSSProperties {
  return { color: m.fg, background: m.bg };
}

export function waitHours(submittedAt: string, now: Date): number {
  return Math.max(0, (now.getTime() - new Date(submittedAt).getTime()) / 3600000);
}

export function waitLabel(h: number): string {
  if (h < 24) return `${Math.round(h)}h`;
  return `${Math.floor(h / 24)}d ${Math.round(h % 24)}h`;
}
